import React, {useState} from "react";
import {
  Box,
  Alert,
  Button,
  TextField,
  Typography,
  Input,
  CircularProgress,
  AppBar,
  Card,
  Checkbox,
  Paper
} from "@mui/material";
import axios from "axios";
import {DataGrid} from "@mui/x-data-grid";
import {BASE_URL, GET, POST} from "APIclient";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MDButton from "components/MDButton";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";
import Snackbar from "@mui/material/Snackbar";

const BiometricFileUpload = () => {
  const [startFrom, setStartFrom] = useState("");
  const [file, setFile] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [markLoading, setMarkLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedAttendance, setSelectedAttendance] = useState({});
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 2000});

  React.useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await GET(
          `admin/getBiometricLeavesReports?offset=${paginationModel.page}&limit=${paginationModel.pageSize}`
        );
        if (response.data.statusCode === 200) {
          setReportData(response.data.data.reportsMissingBioPunchIN.paginatedData || []);
          setSnakColor("success");
          setSnakMessage("Reports fetched successfully!");
          setOpen(true);
        } else {
          setSnakColor("error");
          setSnakMessage("Failed to fetch reports.");
          setOpen(true);
        }
      } catch (err) {
        console.error(err);
        setSnakColor("error");
        setSnakMessage(err.response?.data?.message || "An error occurred while fetching reports.");
        setOpen(true);
      }
    };

    fetchReports();
  }, [paginationModel]);

  const url = `${BASE_URL}/admin/biometricAttendanceFileUpload`;

  const handleFileChange = event => {
    setFile(event.target.files[0]);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!startFrom || !file) {
      setSnakColor("error");
      setSnakMessage("Please fill out all required fields.");
      setOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append("start_from", startFrom);
    formData.append("file", file);

    try {
      setSaveLoading(true);
      setError(null);

      await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        }
      });

      const response = await GET(
        `admin/getBiometricLeavesReports?offset=${paginationModel.page}&limit=${paginationModel.pageSize}`
      );

      if (response.data.statusCode === 200) {
        setReportData(response.data.data.reportsMissingBioPunchIN.paginatedData || []);
        setSnakColor("success");
        setSnakMessage("File uploaded and reports fetched successfully!");
        setOpen(true);
      } else {
        setSnakColor("error");
        setSnakMessage("Failed to fetch reports.");
        setOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnakColor("error");
      setSnakMessage(err.response?.data?.message || "An error occurred. Please try again.");
      setOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAttendanceSelect = (empId, date, type) => {
    console.log("Checkbox selected:", {empId, date, type});
    const key = `${empId}_${date}`;
    setSelectedAttendance(prev => ({
      ...prev,
      [key]: prev[key] === type ? null : type
    }));
  };

  const handleSendEmail = async () => {
    console.log("Send Email button clicked, reportData:", reportData);

    // Create requests array from all reportData entries
    const requests = reportData.map(row => ({
      empId: parseInt(row.empId),
      date: row.date
    }));

    if (requests.length === 0) {
      setSnakColor("error");
      setSnakMessage("No employee data available to send emails.");
      setOpen(true);
      return;
    }

    try {
      setEmailLoading(true);
      const url = "admin/sendBiometricReminderEmail";
      const response = await POST(url, requests);
      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Biometric reminder emails sent successfully!");
        setOpen(true);
      } else {
        setSnakColor("error");
        setSnakMessage(response.data.message || "Failed to send emails.");
        setOpen(true);
      }
    } catch (err) {
      console.error("Send Email error:", err);
      setSnakColor("error");
      setSnakMessage(err.response?.data?.message || "Failed to send emails. Please try again.");
      setOpen(true);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleMarkPresent = async () => {
    console.log("Mark Present button clicked, selectedAttendance:", selectedAttendance);
    const attendanceArray = Object.entries(selectedAttendance)
      .filter(([_, type]) => type !== null)
      .map(([key, type]) => {
        const [empId, date] = key.split("_");
        return {
          empId: parseInt(empId),
          date,
          flag: type === "WFO" ? "P" : "WFH"
        };
      });

    if (attendanceArray.length === 0) {
      setSnakColor("error");
      setSnakMessage("Please select WFO or WFH for at least one employee.");
      setOpen(true);
      return;
    }

    try {
      setMarkLoading(true);
      const response = await POST("admin/markPresentOrWFH", attendanceArray);

      if (response?.data?.statusCode === 200) {
        // Immediately fetch updated reports after marking attendance
        const reportsResponse = await GET(
          `admin/getBiometricLeavesReports?offset=${paginationModel.page}&limit=${paginationModel.pageSize}`
        );
        if (reportsResponse.data.statusCode === 200) {
          setReportData(reportsResponse.data.data.reportsMissingBioPunchIN.paginatedData || []);
          setSnakColor("success");
          setSnakMessage("Attendance marked and reports updated successfully!");
          setOpen(true);
          setSelectedAttendance({});
        } else {
          setSnakColor("error");
          setSnakMessage("Failed to fetch updated reports.");
          setOpen(true);
        }
      } else {
        setSnakColor("error");
        setSnakMessage(response.data.message || "Failed to mark attendance.");
        setOpen(true);
      }
    } catch (err) {
      console.error("Mark Present error:", err);
      setSnakColor("error");
      setSnakMessage(
        err.response?.data?.message ||
          "Failed to mark attendance or fetch reports. Please try again."
      );
      setOpen(true);
    } finally {
      setMarkLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const columns = [
    {field: "empId", headerName: "Employee ID", width: 150},
    {field: "employeeName", headerName: "Employee Name", width: 200},
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || "N/A";
      }
    },
    {
      field: "wfo",
      headerName: "WFO",
      width: 180,
      renderCell: params => (
        <Checkbox
          checked={selectedAttendance[`${params.row.empId}_${params.row.date}`] === "WFO"}
          onChange={() => handleAttendanceSelect(params.row.empId, params.row.date, "WFO")}
          sx={{
            transform: "scale(1.3)",
            "& .MuiSvgIcon-root": {
              border: "1px solid #000",
              borderRadius: "4px"
            },
            "&.Mui-checked .MuiSvgIcon-root": {
              backgroundColor: "#1976d2",
              color: "#1976d2"
            }
          }}
        />
      )
    },
    {
      field: "wfh",
      headerName: "WFH",
      width: 180,
      renderCell: params => (
        <Checkbox
          checked={selectedAttendance[`${params.row.empId}_${params.row.date}`] === "WFH"}
          onChange={() => handleAttendanceSelect(params.row.empId, params.row.date, "WFH")}
          sx={{
            transform: "scale(1.3)",
            "& .MuiSvgIcon-root": {
              border: "1px solid #000",
              borderRadius: "4px"
            },
            "&.Mui-checked .MuiSvgIcon-root": {
              backgroundColor: "#1976d2",
              color: "#1976d2"
            }
          }}
        />
      )
    }
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <Box
              sx={{
                width: 800,
                margin: "50px auto",
                padding: "20px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)"
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "10px",
                  textAlign: "center",
                  fontFamily: properties.fontFamily
                }}
              >
                Biometric File Upload
              </Typography>

              <form onSubmit={handleSubmit}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "10px",
                    fontFamily: properties.fontFamily
                  }}
                >
                  Upload File
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start From"
                    value={startFrom ? dayjs(startFrom) : null}
                    onChange={date => setStartFrom(date ? date.format("YYYY-MM-DD") : "")}
                    sx={{
                      marginBottom: "15px",
                      backgroundColor: "#fff",
                      width: "30ch"
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined",
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>

                <Box sx={{marginBottom: "15px"}}>
                  <Input
                    type="file"
                    inputProps={{accept: ".xlsx, .xls, .csv"}}
                    onChange={handleFileChange}
                    sx={{marginBottom: "5px"}}
                  />
                  <Typography variant="caption" sx={{color: "#777"}}>
                    (Formats accepted: .xlsx, .xls)
                  </Typography>
                </Box>

                <Box sx={{textAlign: "center"}}>
                  <MDButton
                    type="submit"
                    variant="contained"
                    color="info"
                    disabled={saveLoading}
                    sx={{
                      textTransform: "none",
                      background: properties.colors.bgcolorbluethem,
                      color: "#ffffff",
                      fontFamily: properties.fontFamily
                    }}
                  >
                    {saveLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                  </MDButton>
                </Box>
              </form>
            </Box>
            {error && (
              <Typography
                variant="body2"
                color="error"
                sx={{marginTop: "10px", textAlign: "center"}}
              >
                {error}
              </Typography>
            )}
          </Card>
          <Snackbar
            open={open}
            anchorOrigin={{vertical: "top", horizontal: "right"}}
            autoHideDuration={4000}
            onClose={handleClose}
          >
            <Alert
              onClose={handleClose}
              severity={snakColor === "success" ? "success" : "error"}
              variant="filled"
              sx={{width: "100%", color: "#fff"}}
            >
              {snakMessage}
            </Alert>
          </Snackbar>
        </AppBar>

        {reportData.length > 0 && (
          <Box sx={{marginTop: "20px"}}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px"
              }}
            >
              <Typography
                sx={{fontWeight: "bold", fontSize: "16px", fontFamily: properties.fontFamily}}
              >
                Missing Biometric Punch-IN Reports
              </Typography>

              <Box display="flex" gap={2}>
                <MDButton
                  variant="contained"
                  color="success"
                  onClick={handleMarkPresent}
                  disabled={markLoading}
                >
                  {markLoading ? <CircularProgress size={20} color="inherit" /> : "Mark Present"}
                </MDButton>

                <MDButton
                  variant="contained"
                  color="info"
                  sx={{background: properties.colors.bgcolorbluethem}}
                  onClick={handleSendEmail}
                  disabled={emailLoading}
                >
                  {emailLoading ? <CircularProgress size={20} color="inherit" /> : "Send Email"}
                </MDButton>
              </Box>
            </Box>

            <MDBox pb={3}>
              <Paper>
                <DataGrid
                  rows={reportData.map((row, index) => ({
                    id: index,
                    ...row
                  }))}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  autoHeight
                  sx={{
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    height: "600px",
                    "& .MuiDataGrid-scrollbar": {
                      width: "0px !important"
                    }
                  }}
                />
              </Paper>
            </MDBox>
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default BiometricFileUpload;
